import React, { useState } from 'react';
import { 
  Paper, 
  TextField, 
  Button, 
  Box, 
  Typography, 
  InputAdornment,
  IconButton,
  Tooltip,
  FormControlLabel,
  Radio,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Alert
} from '@mui/material';
import { 
  YouTube as YouTubeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { ContentCategory, VideoLanguage, OutputLanguage } from '../services/contentService';
import LanguageSelectorSimple from './LanguageSelectorSimple';

interface VideoInputFormProps {
  onSubmit: (url: string, videoLanguage: VideoLanguage, outputLanguage: OutputLanguage, category?: ContentCategory) => Promise<void>;
  onUpload?: (file: File, videoLanguage: VideoLanguage, outputLanguage: OutputLanguage, category?: ContentCategory) => Promise<void>;
}

// Loading messages to keep user engaged
const loadingMessages = [
  "Analyzing video content...",
  "Extracting key insights...",
  "Preparing your knowledge capsule...",
  "Creating mind map connections...",
  "Generating comprehensive notes...",
  "Almost there! Finalizing your capsule..."
];

const VideoInputForm: React.FC<VideoInputFormProps> = ({
  onSubmit,
  onUpload
}) => {
  // State for multi-step form
  const [activeStep, setActiveStep] = useState<number>(0);
  
  // State for input
  const [url, setUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // State for input method
  const [inputMethod, setInputMethod] = useState<'url' | 'upload'>('url');
  
  // State for file upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // State for category
  const [category, setCategory] = useState<ContentCategory>(ContentCategory.General);
  
  // State for output language (video language is handled automatically)
  const [outputLanguage, setOutputLanguage] = useState<OutputLanguage>(OutputLanguage.English);
  
  // State for loading animation
  const [, setLoadingMessageIndex] = useState<number>(0);
  
  // Handle URL change
  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    setError(null);
  };
  
  // Handle URL clear
  const handleUrlClear = () => {
    setUrl('');
    setError(null);
  };
  
  // Handle input method change
  const handleInputMethodChange = (method: 'url' | 'upload') => {
    setInputMethod(method);
    setError(null);
  };
  
  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setError(null);
    }
  };
  
  // Handle file clear
  const handleFileClear = () => {
    setSelectedFile(null);
    setError(null);
  };
  
  // Handle category change
  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCategory(event.target.value as ContentCategory);
  };
  
  // Handle language change
  const handleLanguageChange = (language: OutputLanguage) => {
    setOutputLanguage(language);
  };
  
  // Handle next step
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    console.log('Form submitted');
    
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    // Start loading message rotation
    setLoadingMessageIndex(0);
    const messageInterval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 3000);
    
    // We'll detect the video language automatically
    // This is just a placeholder - the actual detection happens in youtubeService.ts
    const defaultVideoLanguage = VideoLanguage.English;
    
    console.log('URL:', url);
    console.log('Default video language:', defaultVideoLanguage);
    console.log('Output language:', outputLanguage);
    console.log('Category:', category);
    
    try {
      if (inputMethod === 'url') {
        // Validate URL
        if (!url.trim()) {
          throw new Error('Please enter a YouTube URL');
        }
        
        // Submit URL
        await onSubmit(url, defaultVideoLanguage, outputLanguage, category);
      } else if (inputMethod === 'upload' && onUpload) {
        // Validate file
        if (!selectedFile) {
          throw new Error('Please select a video file');
        }
        
        // Check file size
        if (selectedFile.size > 500 * 1024 * 1024) {
          throw new Error('File size exceeds the 500MB limit. Please select a smaller file.');
        }
        
        // Submit file
        await onUpload(selectedFile, defaultVideoLanguage, outputLanguage, category);
      }
    } catch (err) {
      console.error('Error submitting video:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setActiveStep(0); // Go back to first step on error
    } finally {
      clearInterval(messageInterval);
      setIsLoading(false);
    }
  };
  
  // Check if can proceed to next step
  const canProceedToLanguageSelection = inputMethod === 'url' 
    ? !!url.trim() 
    : (!!selectedFile && selectedFile.size <= 500 * 1024 * 1024);
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <YouTubeIcon sx={{ mr: 1, color: 'error.main' }} />
        <Typography variant="h6">
          Create Knowledge Capsule
        </Typography>
      </Box>
      
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        <Step>
          <StepLabel>Select Category</StepLabel>
        </Step>
        <Step>
          <StepLabel>Enter Video</StepLabel>
        </Step>
        <Step>
          <StepLabel>Choose Language</StepLabel>
        </Step>
        <Step>
          <StepLabel>Create Capsule</StepLabel>
        </Step>
      </Stepper>
      
      {/* Category Selector - Now always visible and first step */}
      {activeStep === 0 && (
        <>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0, 180, 216, 0.05)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary.main">
              Select Content Category
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose the most relevant category for your video to get optimized results
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Studies with subcategories */}
              <Box>
                <FormControlLabel 
                  value={ContentCategory.Educational} 
                  control={
                    <Radio 
                      checked={category === ContentCategory.Educational}
                      onChange={handleCategoryChange}
                      size="small" 
                    />
                  } 
                  label={<Typography variant="subtitle1" fontWeight="bold">Studies</Typography>} 
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  (JEE, NEET, IAS, Other)
                </Typography>
              </Box>
              
              {/* News */}
              <FormControlLabel 
                value={ContentCategory.News} 
                control={
                  <Radio 
                    checked={category === ContentCategory.News}
                    onChange={handleCategoryChange}
                    size="small" 
                  />
                } 
                label={<Typography variant="subtitle1" fontWeight="bold">News</Typography>} 
              />
              
              {/* Dogla-Meter */}
              <FormControlLabel 
                value={ContentCategory.DoglaMeter} 
                control={
                  <Radio 
                    checked={category === ContentCategory.DoglaMeter}
                    onChange={handleCategoryChange}
                    size="small" 
                  />
                } 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Dogla-Meter</Typography>
                    <Tooltip title="Analyzes how much a video manipulates facts or presents biased information">
                      <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                } 
              />
              
              {/* Sports */}
              <FormControlLabel 
                value={ContentCategory.Sports} 
                control={
                  <Radio 
                    checked={category === ContentCategory.Sports}
                    onChange={handleCategoryChange}
                    size="small" 
                  />
                } 
                label={<Typography variant="subtitle1" fontWeight="bold">Sports</Typography>} 
              />
              
              {/* Podcast */}
              <FormControlLabel 
                value={ContentCategory.Podcast} 
                control={
                  <Radio 
                    checked={category === ContentCategory.Podcast}
                    onChange={handleCategoryChange}
                    size="small" 
                  />
                } 
                label={<Typography variant="subtitle1" fontWeight="bold">Podcast</Typography>} 
              />
              
              {/* General */}
              <FormControlLabel 
                value={ContentCategory.General} 
                control={
                  <Radio 
                    checked={category === ContentCategory.General}
                    onChange={handleCategoryChange}
                    size="small" 
                  />
                } 
                label={<Typography variant="subtitle1" fontWeight="bold">General</Typography>} 
              />
            </Box>
          </Box>
          
          {/* Next Button */}
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            sx={{ 
              borderRadius: 4,
              px: 3,
              py: 1
            }}
          >
            Next: Enter Video
          </Button>
        </>
      )}
      
      {/* Step Content - Video Input */}
      {activeStep === 1 && (
        <>
          {/* Input Method Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary.main">
              Enter Your Video
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can either paste a YouTube URL or upload your own video file
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleInputMethodChange('upload')}
                sx={{ 
                  borderRadius: 4, 
                  width: '100%', 
                  mb: 2,
                  py: 1.2
                }}
                startIcon={<CloudUploadIcon />}
              >
                Upload Any Video
              </Button>
              
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  fontWeight: 'bold', 
                  textAlign: 'center',
                  my: 1 
                }}
              >
                OR
              </Typography>
            </Box>
          </Box>
          
          {/* URL Input Field with Switch Button */}
          <Box sx={{ position: 'relative', mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Paste video URL (e.g., https://www.youtube.com/watch?v=dQw4w9WgXcQ)"
              variant="outlined"
              value={url}
              onChange={handleUrlChange}
              disabled={isLoading || inputMethod === 'upload'}
              error={!!error && inputMethod === 'url'}
              helperText={error && inputMethod === 'url' ? error : ''}
              onClick={() => {
                if (inputMethod === 'upload') {
                  handleInputMethodChange('url');
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <YouTubeIcon color={inputMethod === 'url' ? "error" : "disabled"} />
                  </InputAdornment>
                ),
                endAdornment: url && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear input"
                      onClick={handleUrlClear}
                      edge="end"
                      disabled={inputMethod === 'upload'}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 4,
                  opacity: inputMethod === 'url' ? 1 : 0.7,
                  cursor: inputMethod === 'upload' ? 'pointer' : 'text'
                }
              }}
              sx={{ mb: 0 }}
            />
            
            {inputMethod === 'upload' && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleInputMethodChange('url')}
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: 10,
                  zIndex: 1,
                  borderRadius: 4,
                  py: 0.5,
                  px: 2
                }}
              >
                Switch to URL
              </Button>
            )}
          </Box>
          
          {/* Show file upload interface if upload method is selected */}
          {inputMethod === 'upload' && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Max file size: 500MB. Supported formats: MP4, AVI, MOV, WMV, etc.
                </Typography>
              </Box>
              
              <input
                accept="video/*"
                style={{ display: 'none' }}
                id="video-file-input"
                type="file"
                onChange={handleFileSelect}
                disabled={isLoading || !onUpload}
              />
              <label htmlFor="video-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={isLoading || !onUpload}
                  sx={{ 
                    borderRadius: 4,
                    borderStyle: 'dashed',
                    p: 1.5,
                    width: '100%',
                    justifyContent: 'flex-start'
                  }}
                >
                  {selectedFile ? selectedFile.name : 'Select any video file from your device'}
                </Button>
              </label>
              
              {selectedFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" color={selectedFile.size > 500 * 1024 * 1024 ? "error.main" : "text.secondary"} sx={{ flex: 1 }}>
                    {`${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`}
                    {selectedFile.size > 500 * 1024 * 1024 && " (File too large)"}
                  </Typography>
                  
                  <IconButton
                    aria-label="clear file"
                    onClick={handleFileClear}
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              
              {error && inputMethod === 'upload' && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ 
                borderRadius: 4,
                px: 3,
                py: 1
              }}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              disabled={!canProceedToLanguageSelection || isLoading}
              onClick={handleNext}
              sx={{ 
                borderRadius: 4,
                px: 3,
                py: 1
              }}
            >
              Next: Choose Language
            </Button>
          </Box>
        </>
      )}
      
      {/* Language Selection Step */}
      {activeStep === 2 && (
        <>
          {/* Language Selection */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TranslateIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Choose Output Language for Your Capsule
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>This is NOT about the video's language</strong> - We'll detect that automatically. 
              Select the language you want your knowledge capsule to be delivered in.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Mixed languages like Hinglish combine the best of both worlds - technical terms stay in English while explanations are in your regional language for better understanding.
              </Typography>
            </Alert>
            
            <LanguageSelectorSimple
              selectedLanguage={outputLanguage}
              onLanguageChange={handleLanguageChange}
              userSubscription="premium" // For testing, allow all languages
            />
          </Box>
          
          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ 
                borderRadius: 4,
                px: 3,
                py: 1
              }}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{ 
                borderRadius: 4,
                px: 3,
                py: 1
              }}
            >
              Next: Create Capsule
            </Button>
          </Box>
        </>
      )}
      
      {/* Final Step - Create Capsule */}
      {activeStep === 3 && (
        <>
          {/* Summary and Create Button */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0, 180, 216, 0.05)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary.main">
              Review and Create Your Capsule
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please review your selections below and click "Create Capsule" to begin processing
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Video Source:</strong> {inputMethod === 'url' ? 'YouTube URL' : 'Uploaded File'}
                </Typography>
                {inputMethod === 'url' && (
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
                    <strong>URL:</strong> {url}
                  </Typography>
                )}
                {inputMethod === 'upload' && selectedFile && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>File:</strong> {selectedFile.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Capsule Language:</strong> {outputLanguage}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Content Category:</strong> {category}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          
          {!isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={isLoading}
                sx={{ 
                  borderRadius: 4,
                  px: 3,
                  py: 1
                }}
              >
                Back
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{ 
                  borderRadius: 4,
                  px: 4,
                  py: 1.5
                }}
              >
                Create Capsule
              </Button>
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default VideoInputForm;
